// src/encounter/services/use-procedure-search.ts
// Procedure-picker catalog search for the A/P procedure order (PROD-1297). System-aware: the
// picker toggles CPT vs SNOMED, so the hook routes per system —
//   - CPT    → GET /api/Provider/GetProcedure?ProviderId=&searchQuery=
//   - SNOMED → GET /api/Provider/GetSnomed?ProviderId=&searchQuery=
// and when ProviderId is absent it falls back to the full MASTER CPT catalog
//   - GET /api/Master/ProcedureCodesList?searchQuery=    (no ProviderId)
// SNOMED has no master fallback, so a missing providerId on the SNOMED system simply doesn't fetch
// (enabled=false). All responses map to DS CodeSearchResult[] via procedureOptionsToCodeSearchResults
// — procedure rows read `displayName` and may be {code:<Coding>} wrapped (normalizeProcedureCode
// lifts the inner Coding). Min 2-char gate, like medication / radiology search. AbortSignal forwarded.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { procedureOptionsToCodeSearchResults } from '../lib/to-code-search-result';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { CodeDisplayName } from '@/types/patient-chart-api';

const MIN_QUERY_LENGTH = 2;

export type ProcedureSystem = 'cpt' | 'snomed';

function buildUrl(providerId: string | null | undefined, system: ProcedureSystem, q: string): string {
  const sq = `searchQuery=${encodeURIComponent(q)}`;
  if (providerId) {
    const path = system === 'snomed' ? '/api/Provider/GetSnomed' : '/api/Provider/GetProcedure';
    return `${path}?ProviderId=${encodeURIComponent(providerId)}&${sq}`;
  }
  // No providerId: only CPT has a master fallback; SNOMED is gated off (enabled=false below).
  return `/api/Master/ProcedureCodesList?${sq}`;
}

export function useProcedureSearch(
  providerId: string | null | undefined,
  system: ProcedureSystem,
  query: string,
) {
  const q = query.trim();
  // SNOMED has no master fallback — without a providerId there's nowhere to search.
  const hasSource = !!providerId || system === 'cpt';
  return useQuery<CodeSearchResult[]>({
    queryKey: ['procedure-search', providerId ?? null, system, q],
    enabled: hasSource && q.length >= MIN_QUERY_LENGTH,
    queryFn: async ({ signal }) => {
      const res = await get<CodeDisplayName[]>(buildUrl(providerId, system, q), { signal });
      return procedureOptionsToCodeSearchResults(res);
    },
  });
}
